import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import addIcon from "../../assets/images/icons/add-square.svg";
import review from "../../assets/images/icons/sandClock.svg";
import {
  FileUpload,
  GeneralButton,
  GeneralSelectApi,
  Modal,
} from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import {
  useAppDispatch,
  useGetData,
  useGetPersonalInfo,
  usePostData,
} from "../../hooks";
import { GetSpecialtyResponse } from "../../interface/GeneralResponse";
import { OptionType } from "../../interface/OptionTypes";
import { useToast } from "../../Services/ToastContext";
import { SpecialtyCard, SpecialtySkeleton } from "./component";
import { setUserDetails } from "../../redux/reducers/registrationSlice";

interface FormValues {
  SpecialtyId: number | null;
  SubspecialtyId?: number | null;
  File: File | null;
}

const DoctorSpecialty: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isModalReviewOpen, setIsModalReviewOpen] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(
    null,
  );
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<number | null>(
    null,
  );
  const [actionType, setActionType] = useState<"add" | "edit">("add");
  const { data: infoData } = useGetPersonalInfo();

  const {
    data: specialtyData,
    isPending: specialtyLoad,
    refetch,
  } = useGetData<GetSpecialtyResponse[]>(EndPoints.GetSpecialty);

  const validationSchema = z.object({
    SpecialtyId: z.coerce
      .number()
      .nullable()
      .refine((val) => val !== null, {
        message: t("select_your_specialty"),
      }),
    SubspecialtyId: z.coerce.number().nullable(),
    File: z
      .instanceof(File)
      .nullable()
      .refine((file) => file !== null, {
        message: t("required_field"),
      }),
  });

  const methods = useForm<FormValues>({
    defaultValues: { SpecialtyId: null, SubspecialtyId: null, File: null },
    resolver: zodResolver(validationSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = methods;

  const { mutate: createSpecialty, isPending } = usePostData(
    actionType === "add" ? EndPoints.AddSpecialty : EndPoints.EditSpecialty,
  );

  const onSubmit = (data: FormValues) => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    if (data.SubspecialtyId !== null) {
      formData.append("SpecialtyId", String(data.SubspecialtyId));
    } else if (data.SpecialtyId !== null) {
      formData.append("SpecialtyId", String(data.SpecialtyId));
    }
    formData.append("File", file);
    if (selectedSpecialtyId) {
      formData.append("Id", String(selectedSpecialtyId));
    }

    createSpecialty(formData, {
      onSuccess: () => {
        reset();
        setFile(null);
        setUploadSuccess(false);
        refetch();
        setIsModalOpen(false);
        setIsModalReviewOpen(true);
        setSelectedSpecialtyId(null);
      },
      onError: (error: any) =>
        showToast(error.message || t("failed_to_add_Certificate"), "error"),
    });
  };

  const handleSpecialtyChange = (selectedOption: OptionType | null) => {
    if (selectedOption) {
      const specialtyId = selectedOption.value;
      setSelectedSpecialty(specialtyId.toString());
      dispatch(setUserDetails({ SpecialtyId: specialtyId.toString() }));
      if (selectedOption.hasChildren) {
        dispatch(setUserDetails({ SubSpecialtyId: null }));
      } else {
        dispatch(setUserDetails({ SubSpecialtyId: null }));
      }
      setValue("SubspecialtyId", null, { shouldValidate: true });
    } else {
      setSelectedSpecialty(null);
      dispatch(setUserDetails({ SpecialtyId: null, SubSpecialtyId: null }));
      setValue("SpecialtyId", null, { shouldValidate: true });
      setValue("SubspecialtyId", null, { shouldValidate: true });
    }
  };

  const handleSubspecialtyChange = (selectedOption: OptionType | null) => {
    if (selectedOption) {
      dispatch(
        setUserDetails({ SubSpecialtyId: selectedOption.value.toString() }),
      );
      setValue("SubspecialtyId", Number(selectedOption.value), {
        shouldValidate: true,
      });
    } else {
      dispatch(setUserDetails({ SubSpecialtyId: null }));
      setValue("SubspecialtyId", null, { shouldValidate: true });
    }
  };

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <h1 className="font-bold">{t("doctor_specialty")}</h1>
        <GeneralButton
          type="button"
          icon={addIcon}
          title={t("add_new_specialty")}
          onClick={() => {
            setSelectedSpecialtyId(null);
            setActionType("add");
            setIsModalOpen(true);
          }}
          titleClass="max-sm:hidden"
          className="text-sm max-lg:!px-4"
        />
      </div>

      {isModalOpen && (
        <Modal
          header={t("add_new_specialty")}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            methods.reset();
            setFile(null);
          }}
        >
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <GeneralSelectApi
                endpoint={EndPoints.SelectSpecialtyType}
                allParams={{ AccountTypeId: infoData?.Data.AccountType.Id }}
                name="SpecialtyId"
                onChange={handleSpecialtyChange}
                placeholder={t("select_specialty")}
              />
              {errors.SpecialtyId && (
                <p className="text-sm text-error">
                  {errors.SpecialtyId.message}
                </p>
              )}

              {selectedSpecialty && (
                <GeneralSelectApi
                  endpoint={EndPoints.SelectSpecialtyType}
                  allParams={{
                    AccountTypeId: infoData?.Data.AccountType.Id,
                    ParentSpecialtyId: selectedSpecialty || null,
                  }}
                  name="SubspecialtyId"
                  onChange={handleSubspecialtyChange}
                  placeholder={t("select_subspecialty") + " " + t("optional")}
                />
              )}
              {errors.SubspecialtyId && (
                <p className="text-sm text-error">
                  {errors.SubspecialtyId.message}
                </p>
              )}

              <FileUpload
                onFileChange={(selectedFile) => {
                  setFile(selectedFile);
                  setUploadSuccess(true);
                  setValue("File", selectedFile);
                }}
                file={file}
                onCancel={() => {
                  setFile(null);
                  setUploadSuccess(false);
                  setValue("File", null);
                }}
                uploadSuccess={uploadSuccess}
                errorMessageKey={
                  errors.File?.message ? "required_field" : undefined
                }
              />
              <div className="mt-5 flex justify-end gap-4">
                <GeneralButton
                  type="button"
                  fontColor="body-text"
                  title={t("cancel")}
                  color="lite"
                  onClick={() => setIsModalOpen(false)}
                />
                <GeneralButton
                  type="submit"
                  title={t("submit")}
                  isLoading={isPending}
                  className="!px-14"
                  color="blue"
                />
              </div>
            </form>
          </FormProvider>
        </Modal>
      )}

      {isModalReviewOpen && (
        <Modal
          isOpen={isModalReviewOpen}
          onClose={() => {
            methods.reset();
            setFile(null);
            setIsModalReviewOpen(false);
          }}
        >
          <img src={review} alt="review" className="h-40" />
          <h3 className="text-center text-lg font-semibold">
            {t("your_files_under_review")}
          </h3>
          <p className="mb-5 text-center text-body-text">
            {t("you_will_receive_notification")}
          </p>
          <GeneralButton
            type="button"
            title={t("understand")}
            onClick={() => {
              methods.reset();
              setFile(null);
              setIsModalReviewOpen(false);
            }}
          />
        </Modal>
      )}

      <div className="grid grid-cols-1 flex-wrap gap-4 overflow-auto xl:grid-cols-2">
        {specialtyLoad
          ? Array.from({ length: 4 }).map((_, index) => (
              <SpecialtySkeleton key={index} />
            ))
          : specialtyData?.Data?.map((e) => (
              <SpecialtyCard
                key={e.Id}
                item={e}
                replaceClick={() => {
                  setSelectedSpecialtyId(e.Id);
                  setActionType("edit");
                  setIsModalOpen(true);
                }}
              />
            ))}
      </div>
    </div>
  );
};

export default DoctorSpecialty;
